import { useRef, useState, useEffect } from "react";
import { Play, Pause } from "lucide-react";
import { mediaUrl } from "@/lib/media";

const NBARS = 72;

function fmt(s) {
  if (!s || isNaN(s)) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60).toString().padStart(2, "0");
  return `${m}:${sec}`;
}

// Deterministic pseudo-waveform used before real peaks load / if decoding fails.
function fallbackPeaks(seed) {
  let x = 0;
  for (let i = 0; i < (seed || "s").length; i++) x = (x * 31 + seed.charCodeAt(i)) % 9973;
  const out = [];
  for (let i = 0; i < NBARS; i++) {
    x = (x * 1103515245 + 12345) & 0x7fffffff;
    const env = Math.sin((i / NBARS) * Math.PI); // fade in/out at edges
    out.push(0.25 + ((x % 1000) / 1000) * 0.75 * (0.5 + 0.5 * env));
  }
  return out;
}

async function decodePeaks(url) {
  const resp = await fetch(url);
  const buf = await resp.arrayBuffer();
  const Ctx = window.AudioContext || window.webkitAudioContext;
  const ctx = new Ctx();
  const audioBuf = await ctx.decodeAudioData(buf);
  const raw = audioBuf.getChannelData(0);
  const block = Math.floor(raw.length / NBARS) || 1;
  const peaks = [];
  for (let i = 0; i < NBARS; i++) {
    let sum = 0;
    for (let j = 0; j < block; j++) sum += Math.abs(raw[i * block + j] || 0);
    peaks.push(sum / block);
  }
  ctx.close();
  const max = Math.max(...peaks) || 1;
  return peaks.map((p) => Math.max(0.08, p / max));
}

export function AudioPlayer({ song, index, capSeconds, onCapReached }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [cur, setCur] = useState(0);
  const [dur, setDur] = useState(0);
  const [peaks, setPeaks] = useState(() => fallbackPeaks(song.id || song.title || "song"));
  const [hover, setHover] = useState(null); // 0..1 ratio
  const capHitRef = useRef(false);

  const src = mediaUrl(song.audio);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onTime = () => {
      if (capSeconds && a.currentTime >= capSeconds) {
        a.pause();
        a.currentTime = capSeconds;
        setPlaying(false);
        if (!capHitRef.current) { capHitRef.current = true; onCapReached && onCapReached(); }
      }
      setCur(a.currentTime);
      setProgress(a.duration ? (a.currentTime / a.duration) * 100 : 0);
    };
    const onMeta = () => setDur(a.duration);
    const onEnd = () => { setPlaying(false); setProgress(0); setCur(0); };
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("loadedmetadata", onMeta);
    a.addEventListener("ended", onEnd);
    return () => {
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("loadedmetadata", onMeta);
      a.removeEventListener("ended", onEnd);
    };
  }, [capSeconds, onCapReached]);

  useEffect(() => {
    let alive = true;
    decodePeaks(src).then((p) => { if (alive) setPeaks(p); }).catch(() => {});
    return () => { alive = false; };
  }, [src]);

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) { a.pause(); setPlaying(false); }
    else {
      if (capSeconds && a.currentTime >= capSeconds) { a.currentTime = 0; capHitRef.current = false; }
      a.play();
      setPlaying(true);
    }
  };

  const ratioFromEvent = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    return Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
  };

  const seek = (e) => {
    const a = audioRef.current;
    if (!a || !a.duration) return;
    let t = ratioFromEvent(e) * a.duration;
    if (capSeconds) t = Math.min(t, capSeconds);
    a.currentTime = t;
  };

  const playedIdx = Math.round((progress / 100) * NBARS);
  const hoverIdx = hover != null ? Math.round(hover * NBARS) : -1;

  return (
    <div className="flex items-center gap-4 p-4 border border-line bg-ink" data-testid={`audio-player-${index}`}>
      <audio ref={audioRef} src={src} preload="metadata" crossOrigin="anonymous" />
      <button
        onClick={toggle}
        data-testid={`play-song-btn-${index}`}
        className="shrink-0 w-11 h-11 flex items-center justify-center border border-gold text-gold hover:bg-gold hover:text-ink transition-colors"
        aria-label={playing ? "Pause" : "Play"}
      >
        {playing ? <Pause size={18} /> : <Play size={18} className="ml-0.5" />}
      </button>
      <div className="flex-1 min-w-0">
        <div className="flex justify-between items-baseline mb-2 gap-3">
          <span className="font-heading text-sm font-medium text-cream truncate">{song.title}</span>
          <span className="font-mono text-[11px] text-ash shrink-0">
            {capSeconds && <span className="text-gold mr-2 uppercase tracking-wide">30-sec preview</span>}
            {fmt(cur)} / {fmt(dur)}
          </span>
        </div>
        <div
          className="relative h-10 flex items-center gap-[2px] cursor-pointer select-none"
          onClick={seek}
          onMouseMove={(e) => setHover(ratioFromEvent(e))}
          onMouseLeave={() => setHover(null)}
          data-testid={`waveform-${index}`}
        >
          {peaks.map((p, i) => {
            const isPlayed = i < playedIdx;
            const isHoverPreview = hoverIdx >= 0 && i >= playedIdx && i < hoverIdx;
            const color = isPlayed ? "bg-gold" : isHoverPreview ? "bg-gold/40" : "bg-line";
            return (
              <div
                key={i}
                className={`flex-1 rounded-[1px] transition-[height,background-color] duration-150 ${color}`}
                style={{ height: `${Math.round(p * 100)}%` }}
              />
            );
          })}
          {hover != null && (
            <>
              <div
                className="absolute top-0 bottom-0 w-px bg-gold/70 pointer-events-none"
                style={{ left: `${hover * 100}%` }}
              />
              <div
                className="absolute -top-6 font-mono text-[10px] text-gold bg-ink border border-line px-1.5 py-0.5 pointer-events-none -translate-x-1/2 whitespace-nowrap"
                style={{ left: `${hover * 100}%` }}
              >
                {fmt(hover * dur)}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
